import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Star, Briefcase, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { toast } from 'sonner';
import { Skeleton } from './ui/skeleton';

const PastHiresSection = ({ profile }) => {
  const { t } = useTranslation();
  const [pastHires, setPastHires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedHire, setSelectedHire] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPastHires();
  }, [profile]);

  const fetchPastHires = async () => {
    if (!profile) return;
    
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/hires/past', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      setPastHires(response.data.past_hires || []);
    } catch (error) {
      console.error('Error fetching past hires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      
      await api.post('/api/reviews', {
        to_user_id: selectedHire.seeker_id,
        rating: rating,
        comment: comment,
        context: selectedHire.source_type || 'job',  // Use source_type to determine if this is a job or gig
        context_id: selectedHire.job_id
      }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      toast.success('Review submitted successfully!');
      setShowReviewDialog(false);
      setRating(0);
      setComment('');
      setSelectedHire(null);
      
      // Refresh the list
      fetchPastHires();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">{t('pastHires.title')}</h2>
        <Card>
          <div className="overflow-x-auto">
            <Skeleton className="h-48" />
          </div>
        </Card>
      </div>
    );
  }

  if (pastHires.length === 0) {
    return null; // Don't show section if no past hires
  }

  return (
    <>
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">
          {t('pastHires.title')} ({pastHires.length})
        </h2>
        <Card>
          <div className="overflow-x-auto max-w-full">
            <table className="w-full">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold text-sm">{t('pastHires.worker')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm hidden md:table-cell">{t('tables.location')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm">{t('pastHires.job')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm hidden sm:table-cell">{t('pastHires.rating')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm">{t('pastHires.review')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pastHires.map((hire) => (
                  <tr key={hire.id} className="hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {hire.seeker?.name?.[0] || 'W'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            {hire.seeker?.verified && (
                              <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                            )}
                            <span className="font-medium">{hire.seeker?.name}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">
                      {hire.seeker?.town || '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium max-w-[200px] truncate">
                        {hire.job?.title}
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell">
                      {hire.seeker?.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star size={14} className="fill-yellow-500 text-yellow-500" />
                          <span className="text-sm font-medium">{hire.seeker.rating.toFixed(1)}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {hire.already_reviewed ? (
                        <Badge variant="secondary" className="bg-green-50 text-green-700 text-xs">
                          ✓ {t('pastHires.done')}
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedHire(hire);
                            setShowReviewDialog(true);
                          }}
                          data-testid={`write-review-${hire.seeker_id}`}
                        >
                          <Star size={14} className="mr-1" />
                          {t('pastHires.write')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('review.reviewTitle')} {selectedHire?.seeker?.name}</DialogTitle>
            <DialogDescription>
              {t('review.shareExperience')} "{selectedHire?.job?.title}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Star Rating */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t('review.rating')} *</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110"
                    data-testid={`rating-star-${star}`}
                  >
                    <Star
                      size={32}
                      className={star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t('review.yourReview')}</label>
              <Textarea
                placeholder={t('review.placeholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                data-testid="review-comment"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowReviewDialog(false);
                setRating(0);
                setComment('');
                setSelectedHire(null);
              }}
              disabled={submitting}
            >
              {t('dashboard.cancel')}
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={submitting || rating === 0}
              data-testid="submit-review-btn"
            >
              {submitting ? t('review.submitting') : t('review.submitReview')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PastHiresSection;
